import request from '@/utils/request'
import config from './config'

// API重试工具函数
const apiRetry = async (apiCall, maxRetries = 3, delay = 1000) => {
  let lastError

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await apiCall()
    } catch (error) {
      lastError = error
      console.warn(`API调用失败，重试 ${attempt}/${maxRetries}:`, error.message)

      if (attempt < maxRetries) {
        // 等待一段时间后重试
        await new Promise(resolve => setTimeout(resolve, delay * attempt))
      }
    }
  }

  throw lastError
}

// 用户相关接口
export const userApi = {
  login (data) {
    return request({
      url: '/login',
      method: 'post',
      data
    })
  },
  register (data) {
    return request({
      url: '/register',
      method: 'post',
      data
    })
  },

  // 注册并获取token接口
  registerWithToken (data) {
    return request({
      url: '/register/login',
      method: 'post',
      data
    })
  },
  logout () {
    return request({
      url: '/user/logout',
      method: 'post'
    })
  },
  // 获取短信验证码
  getSmsCode (mobile) {
    return request({
      url: `/manage/syscaptcha/getsmscode/${mobile}`,
      method: 'get'
    })
  },
  getUserInfo () {
    return request({
      url: '/getInfo',
      method: 'get'
    })
  },

  // 获取个人详细信息
  getUserProfile () {
    return request({
      url: '/getInfo',
      method: 'get'
    })
  },

  // 更新个人信息
  updateUserProfile (data) {
    return request({
      url: '/system/user/profile',
      method: 'put',
      data
    })
  },

  // 上传头像
  uploadAvatar (file) {
    const formData = new FormData()
    formData.append('file', file)

    return request({
      url: '/files/upload',
      method: 'post',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      timeout: config.uploadTimeout // 使用5分钟超时
    })
  },

  // 获取用户头像地址
  getUserAvatar () {
    return request({
      url: '/user/avatar',
      method: 'get'
    })
  },

  // 更新用户头像信息
  updateUserAvatar (avatarUrl) {
    return request({
      url: '/user/avatar',
      method: 'put',
      data: { avatar: avatarUrl }
    })
  },

  // 上传文件到服务器
  uploadFile (file, onUploadProgress) {
    const formData = new FormData()
    formData.append('file', file)

    return request({
      url: '/files/upload',
      method: 'post',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      timeout: config.uploadTimeout, // 使用配置文件中的上传超时时间
      onUploadProgress: onUploadProgress // 支持上传进度回调
    })
  },

  // 上传文件到公共服务器（用于模版Logo上传）
  uploadCommonFile (file) {
    const formData = new FormData()
    formData.append('file', file)

    return request({
      url: '/common/upload',
      method: 'post',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      timeout: config.uploadTimeout
    })
  },

  // 保存素材信息
  saveMaterial (materialData) {
    return request({
      url: '/manage/membermaterialsinfo',
      method: 'post',
      data: materialData
    })
  },

  // 设置素材的 faceUrl
  setMaterialFaceUrl (materialId, faceUrl) {
    return request({
      url: `/manage/membermaterialsinfo/${materialId}/faceUrl`,
      method: 'put',
      data: { faceUrl: faceUrl }
    })
  },

  // 获取会员等级列表
  getMemberLevelList () {
    return request({
      url: '/manage/memberlevel/list',
      method: 'get'
    })
  },

  // 获取流量充值套餐列表
  getDataPackageList () {
    return request({
      url: '/manage/datapackage/list',
      method: 'get'
    })
  },

  // 获取会员充值二维码
  getMemberRechargeQRCode (data) {
    return request({
      url: '/manage/orderinfo/membeToNativeRecharge',
      method: 'post',
      data
    })
  },

  // 会员充值成功接口
  memberRechargeSuccess (data) {
    return request({
      url: '/manage/orderinfo/recharge-success',
      method: 'post',
      data
    })
  },

  // 获取流量充值二维码
  getTrafficRechargeQRCode (data) {
    return request({
      url: '/manage/orderinfo/membeToNativeRecharge',
      method: 'post',
      data
    })
  },

  // 检查支付状态接口
  checkPaymentStatus (orderId) {
    return request({
      url: `/manage/orderinfo/checkPay/${orderId}`,
      method: 'get'
    })
  },

  // 获取个人信息接口
  getMyInfo () {
    return request({
      url: '/manage/memberinfo/myinfo',
      method: 'get'
    })
  },

  // 获取成员材料类型接口
  getMemberMaterialsTypes () {
    return request({
      url: '/manage/membermaterialsinfo/types',
      method: 'get'
    })
  },

  // 获取成员材料列表接口
  getMemberMaterialsList (materialType = '0', pageNum = 1, pageSize = 20) {
    return request({
      url: '/manage/membermaterialsinfo/mylist',
      method: 'get',
      params: {
        materialType: materialType,
        pageNum: pageNum,
        pageSize: pageSize
      }
    })
  },

  // 删除成员材料接口
  deleteMemberMaterial (materialId) {
    return request({
      url: `/manage/membermaterialsinfo/${materialId}`,
      method: 'delete'
    })
  },

  // 获取消耗记录列表
  getConsumptionRecords (params = {}) {
    return request({
      url: '/manage/resourcechangeinfo/mylist',
      method: 'get',
      params
    })
  },

  // 获取作品记录列表
  getWorkRecords (params = {}) {
    return request({
      url: '/manage/workrecordinfo/mylist',
      method: 'get',
      params
    })
  },

  // 根据类型获取作品记录列表
  getWorkRecordListByType (workType, pageNum = 1, pageSize = 10) {
    return request({
      url: '/manage/workrecordinfo/mylist',
      method: 'get',
      params: {
        workType,
        pageNum,
        pageSize
      }
    })
  },

  // 获取热门作品记录列表（首页使用）
  getHotWorkRecords (params = {}) {
    return request({
      url: '/manage/workrecordinfo/hotlist',
      method: 'get',
      params
    })
  },

  // 获取智能体列表
  getAgentList (pageNum = 1, pageSize = 100) {
    return request({
      url: '/manage/agentinfo/list',
      method: 'get',
      params: {
        pageNum,
        pageSize
      }
    })
  },

  // 获取我的订单列表
  getMyOrderList (orderType, page = 1, pageSize = 10) {
    return request({
      url: '/manage/orderinfo/mylist',
      method: 'get',
      params: {
        orderType,
        pageNum: page,
        pageSize: pageSize
      }
    })
  },

  // 查询用户时长信息
  getUserDurationInfo (userId) {
    return request({
      url: `/manage/memberinfo/userinfo/${userId}`,
      method: 'get'
    })
  },

  // 获取地区列表
  getRegionList () {
    return request({
      url: '/manage/provinceareainfo/list',
      method: 'get'
    })
  },

  // 搜索订单邮箱信息
  searchOrderEmails (keyword, codes) {
    return request({
      url: '/manage/orderemailsinfo/search',
      method: 'get',
      params: {
        keyword: keyword || '',
        codes: codes || ''
      }
    })
  },

  // 计算费用和预计完成时长
  calculateMoney (count) {
    return request({
      url: `/manage/orderemailsinfo/calmoney/${count}`,
      method: 'get'
    })
  },

  // 获取邮件模板列表
  getTemplateList (templateId = 1) {
    return request({
      url: '/manage/orderemailvariables/templatelist',
      method: 'get',
      params: {
        templateId: templateId
      }
    })
  },

  // 获取模板信息列表
  getTemplateInfoList () {
    return request({
      url: '/manage/templateinfo/list',
      method: 'get'
    })
  },

  // 预览模板内容效果
  evaluateScreenTemplateContent (variableList) {
    return request({
      url: '/manage/orderemailvariables/evaluatescreentemplatecontent',
      method: 'post',
      data: variableList
    })
  },

  // 新增订单邮件信息
  createOrderEmailInfo (orderData) {
    return request({
      url: '/manage/orderemailsinfo',
      method: 'post',
      data: orderData
    })
  },

  // 修改订单邮件信息
  updateOrderEmailInfo (orderData) {
    console.log('📝 updateOrderEmailInfo 被调用，方法应该是 PUT')
    console.log('📝 请求数据:', orderData)
    return request({
      url: '/manage/orderemailsinfo',
      method: 'PUT',
      data: orderData
    })
  },

  // 删除订单邮件信息
  deleteOrderEmailInfo (orderId) {
    return request({
      url: `/manage/orderemailsinfo/${orderId}`,
      method: 'delete'
    })
  },

  // 批量新增订单邮件变量
  addOrderEmailVariablesList (variableList) {
    return request({
      url: '/manage/orderemailvariables/addlist',
      method: 'post',
      data: variableList
    })
  },

  // 获取订单邮件信息列表
  getOrderEmailsInfoList (params) {
    return request({
      url: '/manage/orderemailsinfo/list',
      method: 'get',
      params: params
    })
  },

  // 获取订单详情（用于修改）
  getOrderEmailInfoDetail (orderId) {
    return request({
      url: `/manage/orderemailsinfo/getft/${orderId}`,
      method: 'get'
    })
  },

  // 根据订单ID获取模板变量列表
  getTemplateListByOrderId (orderId) {
    return request({
      url: '/manage/orderemailvariables/templatelist',
      method: 'get',
      params: {
        orderId: orderId
      }
    })
  },

  // 邮件推送模块 - 金额为0时的支付接口
  emailPaySuccess (data) {
    return request({
      url: '/manage/orderemailsinfo/emailzeropay',
      method: 'post',
      data: {
        id: data.id || data.orderNo || ''
      }
    })
  },

  // 邮件推送模块 - 查询已发送邮件数量
  queryEmailCount (orderNo) {
    return request({
      url: `/manage/orderemailsinfo/querycount/${orderNo}`,
      method: 'get'
    })
  },

  // 邮件推送模块 - 获取邮件发送详情列表
  getEmailDetail (params) {
    return request({
      url: '/manage/orderemailsinfo/emaildetail',
      method: 'get',
      params: {
        page: params.page || 1,
        pageNum: params.pageNum || params.page || 1,
        pageSize: params.pageSize || 10,
        orderNo: params.orderNo || ''
      }
    })
  },

  // 邮件推送模块 - 获取验证码（通过订单信息）
  getOrderVerificationCode (orderData) {
    return request({
      url: '/manage/orderemailsinfo',
      method: 'post',
      data: orderData
    })
  },

  // 邮件推送模块 - 微信支付接口
  emailToNativeRecharge (data) {
    return request({
      url: '/manage/orderinfo/emailToNativeRecharge',
      method: 'post',
      data: {
        comment: data.comment || '',
        cost: data.cost || 0,
        emailType: data.emailType !== undefined ? data.emailType : 0,
        id: data.id || '',
        operatorType: data.operatorType !== undefined ? data.operatorType : 0,
        payType: data.payType || '0',
        phoneNumber: data.phoneNumber || '',
        price: data.price !== undefined ? data.price : 0,
        totalCount: data.totalCount !== undefined ? data.totalCount : 0,
        userId: data.userId !== undefined ? data.userId : 0
      }
    })
  },

  // 邮件推送模块 - 查询微信支付状态接口
  emailWechatQuery (data) {
    return request({
      url: '/manage/orderinfo/emailToNativeQuery',
      method: 'post',
      data: {
        id: data.id || ''
      }
    })
  },

  // 邮件推送模块 - 查询支付宝支付状态接口
  emailToNativeQuery (data) {
    return request({
      url: '/manage/orderinfo/alipay/emailToNativeQuery',
      method: 'post',
      data: {
        id: data.id || '',
        cost: data.cost || '',
        price: data.price || '',
        totalCount: data.totalCount || '',
        emailType: data.emailType || '',
        payType: data.payType || '',
        operatorType: data.operatorType || '',
        userId: data.userId || '',
        phoneNumber: data.phoneNumber || '',
        comment: data.comment || ''
      }
    })
  },

  // 邮件推送模块 - 支付宝支付接口
  emailAlipayPay (data) {
    console.log('🔵 调用支付宝支付接口 emailAlipayPay')
    console.log('🔵 请求参数:', data)
    console.log('🔵 接口URL: /manage/orderinfo/alipay/webemailpay')
    console.log('🔵 请求方法: POST')
    
    return request({
      url: '/manage/orderinfo/alipay/webemailpay',
      method: 'post',
      data: {
        comment: data.comment || '',
        cost: data.cost || 0,
        emailType: data.emailType !== undefined ? data.emailType : 0,
        id: data.id || '',
        operatorType: data.operatorType !== undefined ? data.operatorType : 0,
        payType: data.payType || '1', // 支付宝支付 payType 为 '1'
        phoneNumber: data.phoneNumber || '',
        price: data.price !== undefined ? data.price : 0,
        totalCount: data.totalCount !== undefined ? data.totalCount : 0,
        userId: data.userId !== undefined ? data.userId : 0
      }
    }).then(response => {
      console.log('🔵 支付宝支付接口响应:', response)
      console.log('🔵 响应中的 pageRedirectionData:', response?.data?.pageRedirectionData || response?.pageRedirectionData)
      return response
    }).catch(error => {
      console.error('❌ 支付宝支付接口调用失败:', error)
      return Promise.reject(error)
    })
  },

  // 添加任务记录接口
  addTask (data) {
    return request({
      url: '/manage/workrecordinfo/addtask',
      method: 'post',
      data
    })
  },

  // 获取avatarflow列表数据
  async getAvatarFlowList (page = 1, pageSize = 10, userid = null) {
    try {
      const skip = (page - 1) * pageSize
      const limit = pageSize
      return await apiRetry(async () => {
        // 创建独立的axios实例来处理外部API
        const axios = require('axios')
        const response = await axios({
          url: `https://h55.futoit.com/avatarflow-api/tasks/list?skip=${skip}&limit=${limit}&userid=${userid}`,
          method: 'get',
          timeout: 30000,
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json, text/html, */*'
          }
        })

        // 处理API返回HTML页面的情况
        if (typeof response.data === 'string' && response.data.includes('<html>')) {
          console.warn('API返回HTML页面，使用模拟数据')
          throw new Error('API返回HTML页面')
        }

        // 处理新的API响应格式
        if (response.data && response.data.code === 200 && response.data.data) {
          console.log('新API数据格式:', response.data)
          return {
            code: 200,
            data: response.data.data, // 提取data数组
            total: response.data.total || response.data.count || response.data.data.length,
            msg: response.data.message || 'success'
          }
        }

        // 兼容旧格式或其他格式
        const dataArray = Array.isArray(response.data) ? response.data : response.data.data || []
        return {
          code: 200,
          data: dataArray,
          total: response.data.total || response.data.count || dataArray.length,
          msg: 'success'
        }
      }, 3, 1000)
    } catch (error) {
      console.error('AvatarFlow API调用失败，使用模拟数据:', error.message)
      // 返回模拟数据作为fallback
      return {
        code: 200,
        data: this.getMockAvatarFlowList(),
        msg: '使用模拟数据'
      }
    }
  },

  // 获取文案复刻列表数据
  async getTextFlowList (page = 1, pageSize = 10, userid = null, status = '') {
    try {
      const skip = (page - 1) * pageSize
      const limit = pageSize
      return await apiRetry(async () => {
        // 创建独立的axios实例来处理外部API
        const axios = require('axios')
        let url = `https://h55.futoit.com/text-avatarflow-api/api/v1/tasks?skip=${skip}&limit=${limit}`
        // 如果提供了userid，添加user_id参数
        if (userid) {
          url += `&user_id=${userid}`
        }
        // 如果有状态筛选，添加status参数（全部时不传status参数）
        if (status && status.trim() !== '') {
          url += `&status=${status}`
        }
        const response = await axios({
          url: url,
          method: 'get',
        })

        // 输出完整的 API 返回结果到控制台
        console.log('response:', response)
       

        // 处理新的API响应格式
        if (response.data && response.data.code === 0 && response.data.data) {
          console.log('新API数据格式:', response.data)
          const responseData = response.data.data
          
          // 从响应中获取tasks数组（支持 response.data.data.tasks 或 response.data.data 格式）
          let taskList = responseData.tasks
          
          return {
            code: 200,
            data: taskList,
            total: responseData?.total,
            msg: response.data.message || 'success'
          }
        }

        // 兼容旧格式或其他格式
        const dataArray = Array.isArray(response.data) ? response.data : response.data.data || []
        console.log('使用兼容格式处理，数据数量:', dataArray.length)
        return {
          code: 200,
          data: dataArray,
          total: response.data.total || response.data.count || dataArray.length,
          msg: 'success'
        }
      }, 3, 1000)
    } catch (error) {
      console.error('TextFlow API调用失败:', error.message)
      // 抛出错误，让调用方处理
      throw error
    }
  },

  // 获取模拟的AvatarFlow列表数据
  getMockAvatarFlowList () {
    return [
      {
        id: 'f7659466-3051-4aec-8b4f-3f9016881db2',
        status: 'completed',
        parameters: {
          input: 'harry小哥',
          end_page: 100,
          platform: 'douyin',
          crawl_type: 'keyword',
          start_page: 1,
          voice_mode: 'clone',
          subtitle_mode: 'custom',
          subtitle_color: '#FFB400',
          cover_title_size: 3,
          subtitle_enabled: true,
          cover_title_color: '#FBCB3D',
          subtitle_position: 3,
          copywriting_polish: true,
          subtitle_font_name: '微软雅黑',
          subtitle_font_level: 3,
          cover_title_position: '中央',
          keep_original_topics: false
        },
        video_count: 783,
        is_all: true,
        result: null,
        error_message: 'None'
      },
      {
        id: '41e360ff-3ac0-419f-8946-b00a8c118d22',
        status: 'failed',
        parameters: {
          input: 'Harry181613',
          end_page: 1,
          platform: 'douyin',
          crawl_type: 'author',
          start_page: 1,
          voice_mode: 'clone',
          subtitle_mode: 'custom',
          subtitle_color: '#FFB400',
          cover_title_size: 3,
          subtitle_enabled: true,
          cover_title_color: '#FBCB3D',
          subtitle_position: 3,
          copywriting_polish: true,
          subtitle_font_name: '微软雅黑',
          subtitle_font_level: 3,
          cover_title_position: '中央',
          keep_original_topics: false
        },
        video_count: 0,
        is_all: true,
        result: null,
        error_message: '爬虫任务完成但未获取到视频数据'
      },
      {
        id: '5f501695-affe-4aca-a7ce-d5edcd5f98d4',
        status: 'failed',
        parameters: {
          input: '83440181975',
          end_page: 1,
          platform: 'douyin',
          crawl_type: 'author',
          start_page: 1,
          voice_mode: 'clone',
          subtitle_mode: 'custom',
          subtitle_color: '#FFB400',
          cover_title_size: 3,
          subtitle_enabled: true,
          cover_title_color: '#FBCB3D',
          subtitle_position: 3,
          copywriting_polish: true,
          subtitle_font_name: '微软雅黑',
          subtitle_font_level: 3,
          cover_title_position: '中央',
          keep_original_topics: false
        },
        video_count: 0,
        is_all: true,
        result: null,
        error_message: '爬虫任务完成但未获取到视频数据'
      },
      {
        id: '6352dfd0-4575-4ffe-be25-8b9f51e51804',
        status: 'completed',
        parameters: {
          input: '朱朱说成长',
          end_page: 20,
          platform: 'douyin',
          crawl_type: 'keyword',
          start_page: 1,
          voice_mode: 'clone',
          subtitle_mode: 'custom',
          subtitle_color: '#FFB400',
          cover_title_size: 3,
          subtitle_enabled: true,
          cover_title_color: '#FBCB3D',
          subtitle_position: 3,
          copywriting_polish: true,
          subtitle_font_name: '微软雅黑',
          subtitle_font_level: 3,
          cover_title_position: '中央',
          keep_original_topics: false
        },
        video_count: 78,
        is_all: true,
        result: null,
        error_message: 'None'
      },
      {
        id: '51def94a-2fa5-4458-84e1-ab500f870bbf',
        status: 'completed',
        parameters: {
          input: 'shiguang5988158',
          end_page: 1,
          platform: 'douyin',
          crawl_type: 'author',
          start_page: 1,
          voice_mode: 'clone',
          subtitle_mode: 'custom',
          subtitle_color: '#FFB400',
          cover_title_size: 3,
          subtitle_enabled: true,
          cover_title_color: '#FBCB3D',
          subtitle_position: 3,
          copywriting_polish: false,
          subtitle_font_name: '微软雅黑',
          subtitle_font_level: 3,
          cover_title_position: '中央',
          keep_original_topics: false
        },
        video_count: 213,
        is_all: true,
        result: null,
        error_message: 'None'
      },
      {
        id: 'TASK_006',
        status: 'completed',
        parameters: {
          input: '中秋节',
          end_page: 1,
          platform: 'douyin',
          crawl_type: 'keyword',
          start_page: 1,
          voice_mode: 'clone',
          subtitle_mode: 'custom',
          subtitle_color: '#FFB400',
          cover_title_size: 3,
          subtitle_enabled: true,
          cover_title_color: '#FBCB3D',
          subtitle_position: 3,
          copywriting_polish: true,
          subtitle_font_name: '微软雅黑',
          subtitle_font_level: 3,
          cover_title_position: '中央',
          keep_original_topics: false
        },
        video_count: 34,
        is_all: true,
        result: null,
        error_message: 'None'
      },
      {
        id: 'ce962976-c329-4a31-8af5-1cfb737f3995',
        status: 'completed',
        parameters: {
          input: '中秋节',
          end_page: 1,
          platform: 'douyin',
          crawl_type: 'keyword',
          start_page: 1,
          voice_mode: 'clone',
          subtitle_mode: 'custom',
          subtitle_color: '#FFB400',
          cover_title_size: 3,
          subtitle_enabled: true,
          cover_title_color: '#FBCB3D',
          subtitle_position: 3,
          copywriting_polish: true,
          subtitle_font_name: '微软雅黑',
          subtitle_font_level: 3,
          cover_title_position: '中央',
          keep_original_topics: false
        },
        video_count: 5,
        is_all: false,
        result: null,
        error_message: 'None'
      },
      {
        id: '3aa55c6f-16e3-4782-9b3e-ecb229f727b4',
        status: 'completed',
        parameters: {
          input: 'FY609066114',
          end_page: 1,
          platform: 'douyin',
          crawl_type: 'author',
          start_page: 1,
          voice_mode: 'clone',
          subtitle_mode: 'custom',
          subtitle_color: '#FFB400',
          cover_title_size: 3,
          subtitle_enabled: true,
          cover_title_color: '#FBCB3D',
          subtitle_position: 3,
          copywriting_polish: true,
          subtitle_font_name: '微软雅黑',
          subtitle_font_level: 3,
          cover_title_position: '中央',
          keep_original_topics: false
        },
        video_count: 352,
        is_all: false,
        result: null,
        error_message: 'None'
      },
      {
        id: 'd52554ba-7d40-493a-a04b-67708ecbf55e',
        status: 'completed',
        parameters: {
          input: '62471672336',
          end_page: 1,
          platform: 'douyin',
          crawl_type: 'author',
          start_page: 1,
          voice_mode: 'clone',
          subtitle_mode: 'custom',
          subtitle_color: '#FFB400',
          cover_title_size: 3,
          subtitle_enabled: true,
          cover_title_color: '#FBCB3D',
          subtitle_position: 3,
          copywriting_polish: true,
          subtitle_font_name: '微软雅黑',
          subtitle_font_level: 3,
          cover_title_position: '中央',
          keep_original_topics: true
        },
        video_count: 75,
        is_all: false,
        result: null,
        error_message: 'None'
      }
    ]
  },

  // 获取任务详情数据
  async getTaskDetails (taskId, skip = 0, limit = 20, status = null) {
    try {
      const params = {
        skip: skip,
        limit: limit
      }

      if (status) {
        params.status = status
      }

      return await apiRetry(async () => {
        // 创建独立的axios实例来处理外部API
        const axios = require('axios')
        const response = await axios({
          url: `https://h55.futoit.com/avatarflow-api/tasks/info/${taskId}`,
          method: 'get',
          params,
          timeout: 15000,
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json, text/html, */*'
          }
        })

        // 处理API返回HTML页面的情况
        if (typeof response.data === 'string' && response.data.includes('<html>')) {
          console.warn('任务详情API返回HTML页面，使用模拟数据')
          throw new Error('API返回HTML页面')
        }

        // 处理新的API响应格式
        if (response.data && response.data.code === 200 && response.data.data) {
          // console.log('新任务详情API数据格式:', response.data)
          return {
            code: 200,
            data: response.data.data, // 提取data数组
            count: response.data.count,
            msg: response.data.message || 'success'
          }
        }

        // 兼容旧格式或其他格式
        return {
          code: 200,
          data: Array.isArray(response.data) ? response.data : response.data.data || [],
          msg: 'success'
        }
      }, 3, 1000)
    } catch (error) {
      console.error('任务详情API调用失败，使用模拟数据:', error.message)
      // 返回模拟数据作为fallback
      return {
        code: 200,
        data: this.getMockTaskDetails(taskId, status),
        msg: '使用模拟数据'
      }
    }
  },

  // 获取模拟的任务详情数据
  getMockTaskDetails (taskId, status = null) {
    const mockData = [
      {
        video_id: '002e480a-520c-4c63-9261-e029ad6b0310',
        title: '#凤观NEWS 8月30日，广东广州，旅游博主杜克带"一哥"来中国了！"一哥"曾是孟加拉人力车夫，杜克助他"逆天改命"，一哥曾说："我一定会去你的国家找你，任何代价都可以"。#孟加拉一哥 #杜克环游世界 #一哥来中国了',
        nickname: '鑫杰好物',
        platform: 'douyin',
        video_text: '凤观NEWS 8月30日，广东广州，旅游博主杜克带"一哥"来中国了！',
        video_url: 'https://www.douyin.com/aweme/v1/play/?video_id=v0300fg10000d2ptqqfog65u506mh54g&line=0&file_id=a3472ff211b146e782b5f32d934f8f77&sign=038a2a271b81ef7d38956cd198d72d75&is_play_url=1&source=PackSourceEnum_SEARCH',
        avatar_video: '',
        progress: 'video_audio_text',
        status: 'failed',
        error_message: '音频&视频中无文案'
      },
      {
        video_id: '002fb51a-0694-4a30-a891-ac571543b96b',
        title: '男人喜欢年轻姑娘是因为她们更好骗吗？ #干货分享 #社会百态 #内容过于真实 #人性 #认知 @DOU+上热门',
        nickname: 'Harry小哥',
        platform: 'douyin',
        video_text: '男人喜欢年轻姑娘，是因为她们更好骗吗？很多大龄未婚单身的女人对此感到不屑甚至唾弃。但我要说，任何试图占据道德制高点的行为，本质上都是为了PUA对方。他们既想要你的钱，又想当老大。当然，我们必须客观地说：年龄并不能完全定义一个人的品行。现在的年轻人乱七八糟的也很多，并不能一概而论年纪大的就是坏人，这确实是个具体问题具体分析的事。但这里面有个非常清晰的逻辑：为什么有人到了三十多岁还单身？很简单，因为他是一个不重视感情的人。无论谈过多少次恋爱，最后都会分手，因为他从不珍惜当下的感情。这才是导致他拖到大龄单身的根本原因。',
        video_url: 'https://www.douyin.com/aweme/v1/play/?video_id=v0200fg10000d1p3lanog65s2n68604g&line=0&file_id=5ca9e9e7f856488e9c29aaea74554727&sign=d36ee79d01df4e8d21b531081d944fa1&is_play_url=1&source=PackSourceEnum_SEARCH',
        avatar_video: '',
        progress: 'avatar_generate',
        status: 'completed',
        error_message: 'None'
      },
      {
        video_id: 'fce6557c-120e-4ba1-9c70-f685bab6fb24',
        title: '#内容过于真实 #内容太过真实',
        nickname: '蓁蓁宝贝呀',
        platform: 'douyin',
        video_text: '春风富贵，这位爷爷看起来好冷啊，我们邀请他进来一起坐，好不好？好呀好呀，爷爷您跟我一起进去暖和一下吧，谢谢你孩子啊。可俺不消费能进去吗？没事的，您可以和我们一起过等等，这是你来消费的地方吗？这啊叔叔外头太冷了，我们想请这位爷爷进来暖和暖和，那不好意思啊，麻烦你出去啊，定啊这招待在店里消费的顾客等等。这位爷爷又没有影响其他人，为什么不能坐这儿休息一下？不行，我家有规定，至少得消费一杯饮品才能做。哎，俺没钱哎，没钱，那就请出去吧。嗯，只要消费一杯饮品就可以做吗？啊，嗯爷爷，您坐着这杯茶，我还没喝，正好给我等一下别人送的饮品牌，说说你，你身上是怪味儿，其他顾客会有意见的，你怎么知道我有意见？对我也有意见，我也是你看吧，大家都嫌弃，你赶快出去吧。嗯你胡说什么呢？我们是对你有意见。大爷您到我这做，大爷您还是到我这儿坐吧，不不不，还是到我这儿来到我这儿来，我拥抱着梦中。',
        video_url: 'https://www.douyin.com/aweme/v1/play/?video_id=v0300fg10000c650cv3c77u034ts1arg&line=0&file_id=1fcb4af7f2b44222933c512043a40aa4&sign=96c1c814fa6a58ed449daf9e80984067&is_play_url=1&source=PackSourceEnum_SEARCH',
        avatar_video: '',
        progress: 'avatar_generate',
        status: 'failed',
        error_message: '当前任务执行已超时（超过24小时）'
      },
      {
        video_id: 'ff02179a-2960-4571-b660-cc8257bcf15c',
        title: '小篮神锐哥找柯冉合影被打断，陈一帆没得分锐哥说我帮你剪辑！#小篮神🏀锐哥 #柯冉',
        nickname: '篮柯一梦',
        platform: 'douyin',
        video_text: '瑞哥想跟科染合影，结果被康老师打断了。后来他又找机会跟科染合影，把科染都搞懵了。陈一帆说昨晚比赛没进球，瑞哥立刻表示要帮他剪辑。但之前瑞哥发过阴阳怪气的帖子，把自己P进科染的照片里嘲讽他。现在又主动跟科染合影蹭流量，要不是科染大气不跟他计较，还一起训练，答应合影。瑞哥却说那些恶意剪辑视频都是别人弄的，希日丽江捧杀他，说他的技术比CBA球员还好，这些都和他没关系。其实昨天下午比赛就结束了，他硬是拖到晚上才跟科染合影，就是想蹭流量。',
        video_url: 'https://www.douyin.com/aweme/v1/play/?video_id=v0200fg10000d2cou8nog65lm4fllshg&line=0&file_id=7b8227dcded9411ea54e549c32d55cf1&sign=442a6ab9940619b54c1d79489ea743d1&is_play_url=1&source=PackSourceEnum_SEARCH',
        avatar_video: '',
        progress: 'avatar_generate',
        status: 'completed',
        error_message: 'None'
      }
    ]

    // 根据taskId返回不同的数据
    let result

    if (taskId === 'f7659466-3051-4aec-8b4f-3f9016881db2') {
      result = mockData
    } else if (taskId === '41e360ff-3ac0-419f-8946-b00a8c118d22') {
      result = mockData.slice(0, 2).map(item => ({
        ...item,
        status: 'failed',
        error_message: '爬虫任务完成但未获取到视频数据'
      }))
    } else {
      result = mockData.map(item => ({
        ...item,
        video_id: `video_${Math.random().toString(36).substr(2, 9)}`,
        title: `任务 ${taskId} 的视频内容`,
        nickname: `user_${taskId.substring(0, 8)}`,
        status: Math.random() > 0.5 ? 'completed' : 'failed',
        progress: Math.random() > 0.5 ? 'avatar_generate' : 'video_audio_text'
      }))
    }

    const normalizedStatus = status ? String(status).toLowerCase() : ''
    if (!normalizedStatus) {
      return result
    }

    return result.filter(item => String(item.status || '').toLowerCase() === normalizedStatus)
  },

  // 完整的任务创建和数据提交方法 (参考 addWrokflow(2).vue)
  async createAvatarTask (formData, userInfo = null) {
    console.log('🚀 开始创建数字人任务')
    console.log('表单数据:', formData)

    try {
      // 1. 数据验证
      const validationResult = this.validateFormData(formData)
      if (!validationResult.isValid) {
        throw new Error(validationResult.message)
      }

      // 2. 数据准备和转换
      const submitData = this.prepareSubmitData(formData, userInfo)

      // 3. 发送请求
      const result = await this.sendTaskRequest(submitData)

      console.log('✅ 任务创建成功:', result)
      return result
    } catch (error) {
      console.error('❌ 任务创建失败:', error.message)
      throw error
    }
  },

  // 数据验证方法
  validateFormData (formData) {
    console.log('=== 开始验证表单数据 ===')

    // 必填字段验证
    const requiredFields = [
      { field: 'platform', name: '视频所属平台' },
      { field: 'avatar_type', name: '生成数字人类型' },
      { field: 'input_type', name: '复刻目标作品来源' }
    ]

    for (const { field, name } of requiredFields) {
      if (!formData[field]) {
        return {
          isValid: false,
          message: `请选择${name}`
        }
      }
    }

    // 输入内容验证
    if (formData.input_type === 'video') {
      // 视频上传模式验证
      if (!formData.files || formData.files.length === 0) {
        return {
          isValid: false,
          message: '请上传视频文件'
        }
      }
      if (formData.files.length > 10) {
        return {
          isValid: false,
          message: '最多只能上传10个视频文件'
        }
      }
    } else {
      // 其他输入模式验证
      if (!formData.input || formData.input.trim() === '') {
        return {
          isValid: false,
          message: '请输入相关内容'
        }
      }
    }

    // 数字人形象验证
    if (formData.avatar_type && !formData.avatar_file) {
      return {
        isValid: false,
        message: '请上传数字人形象'
      }
    }

    // 音色音频验证
    if (formData.voice_mode === 'clone' && !formData.audio_file) {
      return {
        isValid: false,
        message: '请上传音色音频'
      }
    }

    console.log('✅ 表单数据验证通过')
    return { isValid: true }
  },

  // 数据准备和转换方法
  prepareSubmitData (formData, userInfo) {
    console.log('=== 开始准备提交数据 ===')

    // 数据类型转换
    const convertedData = { ...formData }

    // 标题类型转换
    if (convertedData.title_type === 'ai') {
      convertedData.title_type = 1
    } else if (convertedData.title_type === 'original') {
      convertedData.title_type = 2
    }

    // 水印标志转换
    if (convertedData.watermark_flag === true) {
      convertedData.watermark_flag = 1
    } else if (convertedData.watermark_flag === false) {
      convertedData.watermark_flag = 0
    }

    // 字幕位置转换
    if (convertedData.subtitle_position === 'top') {
      convertedData.subtitle_position = 1
    } else if (convertedData.subtitle_position === 'center') {
      convertedData.subtitle_position = 2
    } else if (convertedData.subtitle_position === 'bottom') {
      convertedData.subtitle_position = 3
    }

    // 用户优先级计算
    let priority = 3 // 默认优先级
    if (userInfo && userInfo.memberInfo && userInfo.memberInfo.levelIds) {
      const levelIds = userInfo.memberInfo.levelIds
      const levelArray = levelIds.split(',').map(id => id.trim())
      if (levelArray.includes('4')) {
        priority = 1 // 最高优先级
      } else if (levelArray.includes('3')) {
        priority = 2 // 中等优先级
      }
    }

    // 构建最终提交数据
    const submitData = {
      priority,
      userid: userInfo ? userInfo.user.userId : null,
      platform: convertedData.platform,
      avatar_type: convertedData.avatar_type,
      input_type: convertedData.input_type,
      input: convertedData.input,
      avatar_file: convertedData.avatar_file,
      voice_mode: convertedData.voice_mode,
      voice_speed: convertedData.voice_speed,
      copywriting_polish: convertedData.copywriting_polish,
      bgm_volume: convertedData.bgm_volume,
      title_type: convertedData.title_type,
      keep_original_topics: convertedData.keep_original_topics,
      cover_flag: convertedData.cover_flag,
      cover_picture: convertedData.cover_picture,
      cover_title_size: convertedData.cover_title_size,
      cover_title_color: convertedData.cover_title_color,
      cover_title_position: convertedData.cover_title_position,
      watermark_flag: convertedData.watermark_flag,
      watermark_text: convertedData.watermark_text,
      watermark_position: convertedData.watermark_position,
      watermark_color: convertedData.watermark_color,
      watermark_font_size: convertedData.watermark_font_size,
      watermark_opacity: convertedData.watermark_opacity,
      subtitle_mode: convertedData.subtitle_mode,
      subtitle_enabled: convertedData.subtitle_enabled,
      subtitle_font_name: convertedData.subtitle_font_name,
      subtitle_font_level: convertedData.subtitle_font_level,
      subtitle_color: convertedData.subtitle_color,
      subtitle_position: convertedData.subtitle_position,
      video_selection: convertedData.video_selection,
      audio_file: convertedData.audio_file,
      video_bgm: convertedData.video_bgm,
      endimage_file: convertedData.endimage_file,
      endbgm_file: convertedData.endbgm_file,
      files: convertedData.files
    }

    console.log('✅ 提交数据准备完成:', submitData)
    return submitData
  },

  // 发送任务请求方法
  async sendTaskRequest (data) {
    console.log('🚀 开始发送任务请求')

    try {
      // 检查是否有文件需要上传
      const hasFiles = this.checkForFiles(data)

      if (hasFiles) {
        console.log('📎 检测到文件，使用multipart/form-data上传')
        return await this.uploadWithMultipartData(data)
      } else {
        console.log('📄 无文件，使用普通JSON请求')
        return await this.sendWithoutFiles(data)
      }
    } catch (error) {
      console.error('发送请求失败:', error)
      throw new Error(`网络请求失败: ${error.message}`)
    }
  },

  // 检查是否有文件需要上传
  checkForFiles (data) {
    return data.avatar_file ||
           data.audio_file ||
           data.video_bgm ||
           data.endimage_file ||
           data.endbgm_file ||
           data.cover_picture ||
           (data.files && data.files.length > 0)
  },

  // 文件上传方法
  async uploadWithMultipartData (data) {
    try {
      console.log('📂 开始构建multipart数据')

      // 生成boundary
      const boundary = '----WebKitFormBoundary' + Math.random().toString(36).substr(2)

      // 构建multipart数据
      const multipartData = this.buildMultipartData(data, boundary)

      // 发送请求
      return new Promise((resolve, reject) => {
        const axios = require('axios')
        axios({
          url: config.avatarflowApi + '/tasks/create',
          method: 'POST',
          data: multipartData,
          headers: {
            'Content-Type': 'multipart/form-data; boundary=' + boundary,
            Accept: 'application/json, text/plain, */*'
          },
          timeout: 30000
        })
          .then(response => {
            console.log('✅ 文件上传成功:', response.data)
            resolve(response.data)
          })
          .catch(error => {
            console.error('❌ 文件上传失败:', error)
            reject(error)
          })
      })
    } catch (error) {
      console.error('构建multipart数据失败:', error)
      throw error
    }
  },

  // 无文件请求方法
  async sendWithoutFiles (data) {
    return new Promise((resolve, reject) => {
      const axios = require('axios')
      axios({
        url: config.avatarflowApi + '/tasks/create',
        method: 'POST',
        data: data,
        headers: {
          'Content-Type': 'application/json'
        },
        timeout: 15000
      })
        .then(response => {
          console.log('✅ 请求成功:', response.data)
          resolve(response.data)
        })
        .catch(error => {
          console.error('❌ 请求失败:', error)
          reject(error)
        })
    })
  },

  // 构建multipart数据
  buildMultipartData (data, boundary) {
    const CRLF = '\r\n'
    const delimiter = '--' + boundary
    const closeDelimiter = delimiter + '--'

    let body = ''

    // 添加非文件字段
    Object.keys(data).forEach(key => {
      if (key !== 'avatar_file' && key !== 'audio_file' && key !== 'video_bgm' &&
          key !== 'endimage_file' && key !== 'endbgm_file' && key !== 'files' &&
          key !== 'cover_picture') {
        if (data[key] !== undefined && data[key] !== null) {
          body += delimiter + CRLF
          body += `Content-Disposition: form-data; name="${key}"` + CRLF + CRLF
          body += String(data[key]) + CRLF
        }
      }
    })

    // 计算总长度并构建最终数据
    const bodyBytes = this.stringToUint8Array(body)
    const endBytes = this.stringToUint8Array(CRLF + closeDelimiter + CRLF)
    const result = new ArrayBuffer(bodyBytes.length + endBytes.length)

    const view = new Uint8Array(result)
    view.set(bodyBytes, 0)
    view.set(endBytes, bodyBytes.length)

    return result
  },

  // 字符串转Uint8Array
  stringToUint8Array (str) {
    const utf8 = unescape(encodeURIComponent(str))
    const result = new Uint8Array(utf8.length)
    for (let i = 0; i < utf8.length; i++) {
      result[i] = utf8.charCodeAt(i)
    }
  return result
},


}

// 新闻相关接口
export const newsApi = {
  getNewsList (params) {
    return request({
      url: '/news/list',
      method: 'get',
      params
    })
  },
  getNewsDetail (id) {
    return request({
      url: `/news/detail/${id}`,
      method: 'get'
    })
  }
}

// 推广服务相关接口
export const promotionApi = {
  // 计算推广价格
  async calculatePrice(data) {
    try {
      const axios = require('axios')
      const response = await axios({
        url: 'https://h55.futoit.com/video-marketing-api/api/v1/price/calculate',
        method: 'POST',
        headers: {
          'accept': 'application/json, text/plain, */*',
          'accept-language': 'zh-CN,zh;q=0.9',
          'content-type': 'application/json;charset=UTF-8'
        },
        data: {
          target_count: data.target_count || 5000,
          enable_danmaku: data.enable_danmaku !== undefined ? data.enable_danmaku : false
        },
        timeout: 10000
      })
      
      // 统一返回格式
      if (response.data) {
        // 如果响应数据本身就是结果对象，直接返回
        if (response.data.data) {
          return {
            code: 200,
            data: response.data.data,
            msg: response.data.message || 'success'
          }
        }
        // 如果响应数据直接包含价格信息
        return {
          code: 200,
          data: response.data,
          msg: 'success'
        }
      }
      
      return {
        code: 200,
        data: response.data || {},
        msg: 'success'
      }
    } catch (error) {
      console.error('计算推广价格失败:', error)
      if (error.response) {
        console.error('API错误响应:', error.response.data)
      }
      throw error
    }
  },
  
  // 创建订单
  async createOrder(data) {
    try {
      const axios = require('axios')
      const response = await axios({
        url: 'https://h55.futoit.com/video-marketing-api/api/v1/order/create',
        method: 'POST',
        headers: {
          'accept': 'application/json, text/plain, */*',
          'accept-language': 'zh-CN,zh;q=0.9',
          'content-type': 'application/json;charset=UTF-8'
        },
        data: data,
        timeout: 30000
      })
      
      // 统一返回格式
      if (response.data) {
        return {
          success: response.data.success || false,
          code: response.data.success ? 200 : 500,
          data: response.data.data || response.data,
          message: response.data.message || 'success',
          msg: response.data.message || 'success'
        }
      }
      
      return {
        success: false,
        code: 500,
        data: null,
        message: '创建订单失败',
        msg: '创建订单失败'
      }
    } catch (error) {
      console.error('创建订单失败:', error)
      return {
        success: false,
        code: 500,
        data: null,
        message: error.message || '创建订单失败，请稍后重试',
        msg: error.message || '创建订单失败，请稍后重试'
      }
    }
  },
  
  // 微信支付充值接口
  async commentToNativeRecharge(data) {
    try {
      const response = await request({
        url: '/manage/orderinfo/commentToNativeRecharge', // 去掉 /api 前缀
        method: 'post',
        data: {
          amount: data.amount || 0.01,
          contact: data.contact || '',
          operatorType: data.operatorType || 3,
          orderNo: data.orderNo || '',
          paymentMethod: data.paymentMethod || 'WECHAT',
          targetCount: data.targetCount || 1
        }
      })
      
      console.log('微信支付充值接口响应:', response)
      
      // 统一返回格式
      if (response && response.code === 200) {
        return {
          success: true,
          code: 200,
          data: response.data || response,
          message: response.msg || 'success',
          msg: response.msg || 'success'
        }
      }
      
      return {
        success: false,
        code: response?.code || 500,
        data: null,
        message: response?.msg || '微信支付充值失败',
        msg: response?.msg || '微信支付充值失败'
      }
    } catch (error) {
      console.error('微信支付充值失败:', error)
      return {
        success: false,
        code: 500,
        data: null,
        message: error.message || '微信支付充值失败，请稍后重试',
        msg: error.message || '微信支付充值失败，请稍后重试'
      }
    }
  },

  // 微信支付查询接口
  async commentToNativeQuery(data) {
    try {
      const response = await request({
        url: '/manage/orderinfo/commentToNativeQuery',
        method: 'post',
        data: {
          orderNo: data.orderNo || ''
        }
      })
      
      console.log('微信支付查询接口响应:', response)
      
      // 统一返回格式
      if (response && response.code === 200) {
        return {
          success: true,
          code: 200,
          data: response.data || response,
          message: response.msg || 'success',
          msg: response.msg || 'success'
        }
      }
      
      return {
        success: false,
        code: response?.code || 500,
        data: null,
        message: response?.msg || '查询支付状态失败',
        msg: response?.msg || '查询支付状态失败'
      }
    } catch (error) {
      console.error('查询支付状态失败:', error)
      return {
        success: false,
        code: 500,
        data: null,
        message: error.message || '查询支付状态失败，请稍后重试',
        msg: error.message || '查询支付状态失败，请稍后重试'
      }
    }
  },
  
  // 支付宝支付接口
  async alipayWebCommentPay(data) {
    try {
      const response = await request({
        url: '/manage/orderinfo/alipay/webcommentpay',
        method: 'post',
        data: {
          amount: data.amount || 0.01,
          contact: data.contact || '',
          operatorType: data.operatorType || 3,
          orderNo: data.orderNo || '',
          paymentMethod: data.paymentMethod || 'ALIPAY',
          targetCount: data.targetCount || 1
        }
      })
      
      console.log('支付宝支付接口响应:', response)
      
      // 统一返回格式
      if (response && response.code === 200) {
        return {
          success: true,
          code: 200,
          data: response.data || response,
          message: response.msg || 'success',
          msg: response.msg || 'success'
        }
      }
      
      return {
        success: false,
        code: response?.code || 500,
        data: null,
        message: response?.msg || '支付宝支付失败',
        msg: response?.msg || '支付宝支付失败'
      }
    } catch (error) {
      console.error('支付宝支付失败:', error)
      return {
        success: false,
        code: 500,
        data: null,
        message: error.message || '支付宝支付失败，请稍后重试',
        msg: error.message || '支付宝支付失败，请稍后重试'
      }
    }
  },
  
  // 支付宝支付查询接口
  async alipayWebCommentPayQuery(data) {
    try {
      const response = await request({
        url: '/manage/orderinfo/alipay/webcommentpayquery',
        method: 'post',
        data: {
          amount: data.amount || 0,
          contact: data.contact || '',
          operatorType: data.operatorType || 3,
          orderNo: data.orderNo || '',
          paymentMethod: data.paymentMethod || 'ALIPAY',
          targetCount: data.targetCount || 0
        }
      })
      
      console.log('支付宝支付查询接口响应:', response)
      
      // 统一返回格式
      if (response && response.code === 200) {
        return {
          success: true,
          code: 200,
          data: response.data || response,
          message: response.msg || 'success',
          msg: response.msg || 'success'
        }
      }
      
      return {
        success: false,
        code: response?.code || 500,
        data: null,
        message: response?.msg || '查询支付状态失败',
        msg: response?.msg || '查询支付状态失败'
      }
    } catch (error) {
      console.error('查询支付宝支付状态失败:', error)
      return {
        success: false,
        code: 500,
        data: null,
        message: error.message || '查询支付状态失败，请稍后重试',
        msg: error.message || '查询支付状态失败，请稍后重试'
      }
    }
  },
  
  // 金额为0时的支付成功接口
  async commentToJsapiPaySucess(data) {
    try {
      const response = await request({
        url: '/manage/orderinfo/commentToJsapiPaySucess',
        method: 'post',
        data: {
          orderNo: data.orderNo || ''
        }
      })
      
      console.log('支付成功接口响应:', response)
      
      // 统一返回格式
      if (response && response.code === 200) {
        return {
          success: true,
          code: 200,
          data: response.data || response,
          message: response.msg || 'success',
          msg: response.msg || 'success'
        }
      }
      
      return {
        success: false,
        code: response?.code || 500,
        data: null,
        message: response?.msg || '支付处理失败',
        msg: response?.msg || '支付处理失败'
      }
    } catch (error) {
      console.error('支付处理失败:', error)
      return {
        success: false,
        code: 500,
        data: null,
        message: error.message || '支付处理失败，请稍后重试',
        msg: error.message || '支付处理失败，请稍后重试'
      }
    }
  },

  // 批量查询订单
  async batchQueryOrders(orderNos) {
    try {
      const axios = require('axios')
      const response = await axios({
        url: 'https://h55.futoit.com/video-marketing-api/api/v1/order/batch-query',
        method: 'POST',
        headers: {
          'accept': 'application/json, text/plain, */*',
          'accept-language': 'zh-CN,zh;q=0.9',
          'content-type': 'application/json;charset=UTF-8'
        },
        data: {
          order_nos: Array.isArray(orderNos) ? orderNos : []
        },
        timeout: 30000
      })
      
      // 统一返回格式
      if (response.data) {
        return {
          success: true,
          code: 200,
          data: response.data.data || response.data,
          message: response.data.message || 'success',
          msg: response.data.message || 'success'
        }
      }
      
      return {
        success: false,
        code: 500,
        data: null,
        message: '批量查询订单失败',
        msg: '批量查询订单失败'
      }
    } catch (error) {
      console.error('批量查询订单失败:', error)
      if (error.response) {
        console.error('API错误响应:', error.response.data)
      }
      return {
        success: false,
        code: 500,
        data: null,
        message: error.message || '批量查询订单失败，请稍后重试',
        msg: error.message || '批量查询订单失败，请稍后重试'
      }
    }
  },

  // 查询订单（支持按手机号或订单号查询）
  async queryOrders(page = 1, pageSize = 10, contact = '', orderNo = '', paymentStatus = '', orderStatus = '') {
    try {
      const axios = require('axios')
      
      // 构建请求参数
      const requestData = {
        page: page,
        page_size: pageSize
      }
      
      // 统一使用 contact 字段（参考小程序逻辑，不区分订单号和手机号）
      if (contact) {
        requestData.contact = contact
      } else if (orderNo) {
        // 如果只有 orderNo，也作为 contact 传递（兼容旧代码）
        requestData.contact = orderNo
      }
      
      // 如果有支付状态参数，添加到请求中
      if (paymentStatus) {
        requestData.payment_status = paymentStatus
      }
      
      // 如果有订单状态参数，添加到请求中（参考小程序逻辑）
      if (orderStatus) {
        requestData.order_status = orderStatus
      }
      
      const response = await axios({
        url: 'https://h55.futoit.com/video-marketing-api/api/v1/order/query',
        method: 'POST',
        headers: {
          'accept': 'application/json, text/plain, */*',
          'accept-language': 'zh-CN,zh;q=0.9',
          'content-type': 'application/json;charset=UTF-8'
        },
        data: requestData,
        timeout: 30000
      })
      
      // 统一返回格式
      if (response.data) {
        // 提取 total 字段，可能在不同的位置
        let total = 0
        if (response.data.total !== undefined) {
          total = response.data.total
        } else if (response.data.count !== undefined) {
          total = response.data.count
        } else if (response.data.data && typeof response.data.data === 'object' && response.data.data.total !== undefined) {
          total = response.data.data.total
        } else if (Array.isArray(response.data.data || response.data)) {
          total = (response.data.data || response.data).length
        }
        
        return {
          success: true,
          code: 200,
          data: response.data.data || response.data,
          message: response.data.message || 'success',
          msg: response.data.message || 'success',
          total: total
        }
      }
      
      return {
        success: false,
        code: 500,
        data: null,
        message: '查询订单失败',
        msg: '查询订单失败'
      }
    } catch (error) {
      console.error('查询订单失败:', error)
      if (error.response) {
        console.error('API错误响应:', error.response.data)
      }
      return {
        success: false,
        code: 500,
        data: null,
        message: error.message || '查询订单失败，请稍后重试',
        msg: error.message || '查询订单失败，请稍后重试'
      }
    }
  },

  // 修改订单支付方式
  async updatePaymentMethod(orderNo, paymentMethod) {
    try {
      const axios = require('axios')
      const response = await axios({
        url: 'https://h55.futoit.com/video-marketing-api/api/v1/payment/pay',
        method: 'POST',
        headers: {
          'accept': 'application/json, text/plain, */*',
          'accept-language': 'zh-CN,zh;q=0.9',
          'content-type': 'application/json;charset=UTF-8'
        },
        data: {
          order_no: orderNo,
          payment_method: paymentMethod // 'wechat' 或 'alipay'
        },
        timeout: 30000
      })
      
      // 统一返回格式
      if (response.data) {
        return {
          success: response.data.success !== false,
          code: response.data.code || (response.data.success !== false ? 200 : 500),
          data: response.data.data || response.data,
          message: response.data.message || 'success',
          msg: response.data.message || 'success'
        }
      }
      
      return {
        success: false,
        code: 500,
        data: null,
        message: '修改支付方式失败',
        msg: '修改支付方式失败'
      }
    } catch (error) {
      console.error('修改支付方式失败:', error)
      if (error.response) {
        console.error('API错误响应:', error.response.data)
      }
      return {
        success: false,
        code: 500,
        data: null,
        message: error.message || '修改支付方式失败，请稍后重试',
        msg: error.message || '修改支付方式失败，请稍后重试'
      }
    }
  },

  // 查询订单列表（不需要手机号或订单号，直接分页查询所有订单）
  async queryOrdersList(page = 1, pageSize = 10, contact = '') {
    try {
      const axios = require('axios')
      
      // 构建请求参数，包含 page、page_size 和可选的 contact
      const requestData = {
        page: page,
        page_size: pageSize
      }
      
      // 如果提供了contact参数，添加到请求中
      if (contact) {
        requestData.contact = contact
      }
      
      console.log('查询订单列表接口请求参数:', requestData)
      
      const response = await axios({
        url: 'https://h55.futoit.com/video-marketing-api/api/v1/order/query',
        method: 'POST',
        headers: {
          'accept': 'application/json, text/plain, */*',
          'accept-language': 'zh-CN,zh;q=0.9',
          'content-type': 'application/json;charset=UTF-8'
        },
        data: requestData,
        timeout: 30000
      })
      
      console.log('查询订单列表接口原始响应:', response)
      console.log('查询订单列表接口响应数据:', response.data)
      
      // 统一返回格式
      // response.data 是服务器返回的JSON数据
      // 如果服务器返回 {code: 0, success: true, data: {list: [...], total: 5}}
      // 那么 response.data = {code: 0, success: true, data: {list: [...], total: 5}}
      if (response && response.data !== undefined && response.data !== null) {
        // 处理各种可能的数据结构
        let resultData = null
        let totalCount = 0
        const serverData = response.data // 服务器返回的数据
        
        // 情况1: 响应本身就是数组
        if (Array.isArray(serverData)) {
          resultData = serverData
          totalCount = serverData.length
          console.log('响应数据是数组，长度:', resultData.length)
        }
        // 情况2: 响应有 data.list 字段且是数组（这是最常见的格式：{code: 0, data: {list: [...], total: 5}}）
        else if (serverData.data && serverData.data.list && Array.isArray(serverData.data.list)) {
          resultData = serverData.data.list
          totalCount = serverData.data.total || serverData.data.count || serverData.data.list.length
          console.log('响应数据在 data.data.list 中，长度:', resultData.length, '总数:', totalCount)
        }
        // 情况3: 响应有 data 字段且是数组
        else if (serverData.data && Array.isArray(serverData.data)) {
          resultData = serverData.data
          totalCount = serverData.total || serverData.count || serverData.data.length
          console.log('响应数据在 data.data 中，长度:', resultData.length)
        }
        // 情况4: 响应有 list 字段且是数组（直接是 {list: [...], total: 5}）
        else if (serverData.list && Array.isArray(serverData.list)) {
          resultData = serverData.list
          totalCount = serverData.total || serverData.count || serverData.list.length
          console.log('响应数据在 data.list 中，长度:', resultData.length, '总数:', totalCount)
        }
        // 情况5: 响应有 orders 字段且是数组
        else if (serverData.orders && Array.isArray(serverData.orders)) {
          resultData = serverData.orders
          totalCount = serverData.total || serverData.count || serverData.orders.length
          console.log('响应数据在 data.orders 中，长度:', resultData.length)
        }
        // 情况6: 响应数据是对象但不是数组
        else if (typeof serverData === 'object' && !Array.isArray(serverData)) {
          // 如果对象本身就是订单数据，转换为数组
          if (serverData.order_no || serverData.orderNo || serverData.id) {
            resultData = [serverData]
            totalCount = 1
            console.log('响应数据是单个订单对象，已转换为数组')
          } else {
            // 保留原始数据，让前端处理
            resultData = serverData
            totalCount = serverData.total || serverData.count || 0
            console.log('响应数据是对象，保留原样:', resultData)
          }
        }
        // 其他情况
        else {
          resultData = serverData
          totalCount = Array.isArray(resultData) ? resultData.length : 0
          console.log('响应数据格式未识别，使用原样:', resultData)
        }
        
        // 确定返回的code：优先使用根级别的code，然后是response.data.code，最后使用200
        // 注意：code: 0 表示成功，code: 200 也表示成功
        const responseCode = serverData.code !== undefined 
          ? (serverData.code === 0 ? 200 : serverData.code)  // 将code: 0转换为200以保持一致性
          : ((serverData.data && typeof serverData.data === 'object' && !Array.isArray(serverData.data) && serverData.data.code !== undefined)
            ? (serverData.data.code === 0 ? 200 : serverData.data.code)
            : 200)
        
        // 确定total：优先使用serverData.data.total，然后是serverData.total，最后是计算出的totalCount
        const responseTotal = (serverData.data && typeof serverData.data === 'object' && !Array.isArray(serverData.data))
          ? (serverData.data.total || serverData.data.count || totalCount)
          : (serverData.total || serverData.count || totalCount)
        
        return {
          success: serverData.success !== undefined ? serverData.success : true,
          code: responseCode,
          data: resultData,
          message: serverData.message || (serverData.data && typeof serverData.data === 'object' && !Array.isArray(serverData.data) && serverData.data.message) || 'success',
          msg: serverData.message || (serverData.data && typeof serverData.data === 'object' && !Array.isArray(serverData.data) && serverData.data.message) || 'success',
          total: responseTotal
        }
      }
      
      // 如果没有响应数据
      console.warn('接口返回数据为空或null')
      return {
        success: false,
        code: 500,
        data: null,
        message: '查询订单列表失败：响应数据为空',
        msg: '查询订单列表失败：响应数据为空',
        total: 0
      }
    } catch (error) {
      console.error('查询订单列表失败:', error)
      if (error.response) {
        console.error('API错误响应状态:', error.response.status)
        console.error('API错误响应数据:', error.response.data)
      } else if (error.request) {
        console.error('请求已发送但没有收到响应:', error.request)
      } else {
        console.error('请求配置错误:', error.message)
      }
      return {
        success: false,
        code: 500,
        data: null,
        message: error.message || '查询订单列表失败，请稍后重试',
        msg: error.message || '查询订单列表失败，请稍后重试',
        total: 0
      }
    }
  }
}

// 语音克隆相关接口
export const voiceCloneApi = {
  // 语音克隆接口
  async cloneVoice (formData) {
    console.log('🎤 开始语音克隆请求')
    
    try {
      // 生成随机task_id
      const taskId = 'voice_clone_' + Math.random().toString(36).substr(2, 9)
      
      // 构建multipart/form-data
      const boundary = '----WebKitFormBoundary' + Math.random().toString(36).substr(2)
      const multipartData = this.buildVoiceCloneMultipartData(formData, taskId, boundary)
      
      console.log('multipart数据构建完成，大小:', multipartData.byteLength)
      
      // 使用独立的API地址，直接使用axios避免拦截器
      const axios = require('axios')
      const response = await axios({
        url: 'https://h55.futoit.com/audio-clone-master/api/v1/clone_voice',
        method: 'POST',
        data: multipartData,
        responseType: 'arraybuffer',
        headers: {
          'Content-Type': 'multipart/form-data; boundary=' + boundary,
          'Accept': 'application/json',
          'Accept-Language': 'zh-CN,zh;q=0.9',
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        timeout: 300000 // 5分钟超时
      })
      
      console.log('语音克隆响应:', response)
      console.log('响应状态码:', response.status)
      
      // 处理响应数据
      let responseData = response.data
      
      // 检查是否为ArrayBuffer响应
      if (responseData instanceof ArrayBuffer) {
        console.log('开始转换ArrayBuffer为字符串...')
        const text = this.arrayBufferToString(responseData)
        console.log('转换后的文本:', text)
        responseData = JSON.parse(text)
        console.log('解析后的JSON:', responseData)
      } else if (typeof responseData === 'string') {
        console.log('响应是字符串，直接解析JSON')
        responseData = JSON.parse(responseData)
      } else {
        console.log('响应已经是对象:', responseData)
      }
      
      if (responseData && responseData.code === 0) {
        return {
          code: 0,
          success: true,
          message: responseData.message || '语音克隆任务提交成功',
          data: {
            task_id: responseData.data?.task_id || taskId,
            status: responseData.data?.status || 'submitted',
            file_name: responseData.data?.file_name || 'voice_clone.mp3',
            created_at: responseData.data?.created_at
          }
        }
      } else {
        throw new Error(responseData.message || '语音克隆失败')
      }
    } catch (error) {
      console.error('语音克隆请求失败:', error)
      throw new Error(`语音克隆失败: ${error.message}`)
    }
  },

  // 构建语音克隆multipart/form-data数据
  buildVoiceCloneMultipartData (formData, taskId, boundary) {
    const CRLF = '\r\n'
    const delimiter = '--' + boundary
    const closeDelimiter = delimiter + '--'

    // 根据文件扩展名确定Content-Type
    const getContentType = (filename) => {
      const ext = filename.toLowerCase().split('.').pop()
      switch (ext) {
        case 'wav':
          return 'audio/wav'
        case 'mp3':
          return 'audio/mpeg'
        case 'm4a':
          return 'audio/mp4'
        case 'aac':
          return 'audio/aac'
        default:
          return 'application/octet-stream'
      }
    }

    // 构建表单数据
    let body = ''

    // text_content字段 - 输入的文本
    body += delimiter + CRLF
    body += 'Content-Disposition: form-data; name="text_content"' + CRLF
    body += 'Content-Type: text/plain' + CRLF + CRLF
    body += formData.textContent + CRLF

    // voice_speed字段 - 语速
    body += delimiter + CRLF
    body += 'Content-Disposition: form-data; name="voice_speed"' + CRLF
    body += 'Content-Type: text/plain' + CRLF + CRLF
    body += formData.speed.toString() + CRLF

    // userid字段 - 用户ID
    body += delimiter + CRLF
    body += 'Content-Disposition: form-data; name="userid"' + CRLF
    body += 'Content-Type: text/plain' + CRLF + CRLF
    body += (formData.userId || '') + CRLF

    // task_id字段 - 任务ID
    body += delimiter + CRLF
    body += 'Content-Disposition: form-data; name="task_id"' + CRLF
    body += 'Content-Type: text/plain' + CRLF + CRLF
    body += taskId + CRLF

    // audio_file字段 - 上传的音频文件
    body += delimiter + CRLF
    body += 'Content-Disposition: form-data; name="audio_file"; filename="' + formData.fileName + '"' + CRLF
    body += 'Content-Type: ' + getContentType(formData.fileName) + CRLF + CRLF

    // 将字符串转换为Uint8Array
    const headerBytes = this.stringToUint8Array(body)
    const endBytes = this.stringToUint8Array(CRLF + closeDelimiter + CRLF)

    // 计算总长度
    const totalLength = headerBytes.length + formData.fileData.byteLength + endBytes.length

    // 创建最终的ArrayBuffer
    const result = new ArrayBuffer(totalLength)
    const view = new Uint8Array(result)

    let offset = 0

    // 复制header
    view.set(headerBytes, offset)
    offset += headerBytes.length

    // 复制文件数据
    view.set(new Uint8Array(formData.fileData), offset)
    offset += formData.fileData.byteLength

    // 复制结束标记
    view.set(endBytes, offset)

    return result
  },

  // ArrayBuffer转字符串（兼容性处理）
  arrayBufferToString (buffer) {
    try {
      // 优先使用TextDecoder（如果可用）
      if (typeof TextDecoder !== 'undefined') {
        const decoder = new TextDecoder('utf-8')
        return decoder.decode(buffer)
      }
    } catch (e) {
      console.log('TextDecoder不可用，使用兼容方法')
    }

    // 兼容方法：手动转换
    const bytes = new Uint8Array(buffer)
    let result = ''
    for (let i = 0; i < bytes.length; i++) {
      result += String.fromCharCode(bytes[i])
    }
    return result
  },

  // 字符串转Uint8Array
  stringToUint8Array (str) {
    const utf8 = unescape(encodeURIComponent(str))
    const result = new Uint8Array(utf8.length)
    for (let i = 0; i < utf8.length; i++) {
      result[i] = utf8.charCodeAt(i)
    }
    return result
  }
}

// 合同检查相关接口
export const contractCheckApi = {
  // 上传文件到Coze（带重试机制）
  async uploadFileToCoze(file) {
    return await apiRetry(async () => {
      return await this.uploadFileToCozeInternal(file)
    }, 3, 1000)
  },

  // 内部上传文件方法
  async uploadFileToCozeInternal(file) {
    console.log('📁 开始上传文件到Coze')
    
    try {
      const formData = new FormData()
      formData.append('file', file)
      
      const response = await fetch('https://api.coze.cn/v1/files/upload', {
        method: 'POST',
        headers: {
          'authorization': 'Bearer sat_VCyJL2KbCYoJdJHGTAPP3zAQ9QB9YB3u9RpNjR7H81SjhE1FifxjRwUXKIuL4sEq'
        },
        body: formData,
        mode: 'cors',
        credentials: 'omit'
      })
      
      console.log('上传响应状态:', response.status, response.statusText)
      
      if (!response.ok) {
        const errorText = await response.text()
        console.error('上传失败响应内容:', errorText)
        throw new Error(`上传失败: ${response.status} ${response.statusText} - ${errorText}`)
      }
      
      const result = await response.json()
      console.log('✅ 文件上传成功:', result)
      
      if (result.code === 0) {
        return {
          code: 0,
          success: true,
          message: '文件上传成功',
          data: {
            file_id: result.data?.id || result.data?.file_id,
            file_name: result.data?.name || file.name,
            file_size: result.data?.size || file.size
          }
        }
      } else {
        throw new Error(result.message || '文件上传失败')
      }
    } catch (error) {
      console.error('❌ 文件上传失败:', error)
      
      // 根据错误类型提供更详细的错误信息
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('网络连接失败，请检查网络连接或稍后重试')
      } else if (error.message.includes('CORS')) {
        throw new Error('跨域请求被阻止，请检查网络配置')
      } else if (error.message.includes('timeout')) {
        throw new Error('请求超时，请稍后重试')
      } else {
        throw new Error(`文件上传失败: ${error.message}`)
      }
    }
  },

  // 运行合同检查工作流（带重试机制）
  async runContractCheckWorkflow(fileId, input) {
    return await apiRetry(async () => {
      return await this.runContractCheckWorkflowInternal(fileId, input)
    }, 3, 1000)
  },

  // 内部工作流运行方法
  async runContractCheckWorkflowInternal(fileId, input) {
    
    try {
      const requestBody = {
        parameters: {
          input: input,
          file: {
            file_id: fileId
          }
        },
        workflow_id: '7485934439362002998'
      }
      
      console.log('工作流请求参数:', requestBody)
      
      const response = await fetch('https://api.coze.cn/v1/workflow/run', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'Bearer sat_VCyJL2KbCYoJdJHGTAPP3zAQ9QB9YB3u9RpNjR7H81SjhE1FifxjRwUXKIuL4sEq'
        },
        body: JSON.stringify(requestBody),
        mode: 'cors',
        credentials: 'omit'
      })
      
      console.log('工作流响应状态:', response.status, response.statusText)
      
      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`工作流运行失败: ${response.status} ${response.statusText} - ${errorText}`)
      }
      
      const result = await response.json()
      return result
      
      if (result.code === 0) {
        return {
          code: 0,
          success: true,
          message: '合同检查完成',
          data: {
            task_id: result.data?.task_id,
            status: result.data?.status,
            result: result.data?.result
          }
        }
      } else {
        throw new Error(result.message || '合同检查失败')
      }
    } catch (error) {
      console.error('❌ 合同检查失败:', error)
      
      // 根据错误类型提供更详细的错误信息
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('网络连接失败，请检查网络连接或稍后重试')
      } else if (error.message.includes('CORS')) {
        throw new Error('跨域请求被阻止，请检查网络配置')
      } else if (error.message.includes('timeout')) {
        throw new Error('请求超时，请稍后重试')
      } else {
        throw new Error(`合同检查失败: ${error.message}`)
      }
    }
  },

  // 获取文件内容（带重试机制）
  async getFileContent(fileId) {
    return await apiRetry(async () => {
      return await this.getFileContentInternal(fileId)
    }, 3, 1000)
  },

  // 内部获取文件内容方法
  async getFileContentInternal(fileId) {
    console.log('📄 开始获取文件内容')
    
    try {
      const response = await fetch(`https://api.coze.cn/v1/files/${fileId}`, {
        method: 'GET',
        headers: {
          'authorization': 'Bearer sat_VCyJL2KbCYoJdJHGTAPP3zAQ9QB9YB3u9RpNjR7H81SjhE1FifxjRwUXKIuL4sEq'
        },
        mode: 'cors',
        credentials: 'omit'
      })
      
      console.log('文件内容响应状态:', response.status, response.statusText)
      
      if (!response.ok) {
        const errorText = await response.text()
        console.error('获取文件内容失败响应内容:', errorText)
        throw new Error(`获取文件内容失败: ${response.status} ${response.statusText} - ${errorText}`)
      }
      
      const result = await response.json()
      console.log('✅ 文件内容获取成功:', result)
      
      if (result.code === 0) {
        return {
          code: 0,
          success: true,
          message: '文件内容获取成功',
          data: {
            content: result.data?.content || result.data?.text || result.data?.content_text || '',
            file_name: result.data?.name || result.data?.file_name || '',
            file_type: result.data?.type || result.data?.file_type || ''
          }
        }
      } else {
        throw new Error(result.message || '获取文件内容失败')
      }
    } catch (error) {
      console.error('❌ 获取文件内容失败:', error)
      
      // 根据错误类型提供更详细的错误信息
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('网络连接失败，请检查网络连接或稍后重试')
      } else if (error.message.includes('CORS')) {
        throw new Error('跨域请求被阻止，请检查网络配置')
      } else if (error.message.includes('timeout')) {
        throw new Error('请求超时，请稍后重试')
      } else {
        throw new Error(`获取文件内容失败: ${error.message}`)
      }
    }
  },

  // 备用API调用方法（使用axios作为fallback）
  async uploadFileToCozeFallback(file) {
    console.log('📁 使用备用方法上传文件到Coze')
    
    try {
      const axios = require('axios')
      const formData = new FormData()
      formData.append('file', file)
      
      const response = await axios({
        url: 'https://api.coze.cn/v1/files/upload',
        method: 'POST',
        data: formData,
        headers: {
          'authorization': 'Bearer sat_VCyJL2KbCYoJdJHGTAPP3zAQ9QB9YB3u9RpNjR7H81SjhE1FifxjRwUXKIuL4sEq'
        },
        timeout: 30000
      })
      
      console.log('✅ 备用方法文件上传成功:', response.data)
      
      if (response.data.code === 0) {
        return {
          code: 0,
          success: true,
          message: '文件上传成功',
          data: {
            file_id: response.data.data?.id || response.data.data?.file_id,
            file_name: response.data.data?.name || file.name,
            file_size: response.data.data?.size || file.size
          }
        }
      } else {
        throw new Error(response.data.message || '文件上传失败')
      }
    } catch (error) {
      console.error('❌ 备用方法文件上传失败:', error)
      throw new Error(`备用方法文件上传失败: ${error.message}`)
    }
  }
}

// 系统相关接口
export const systemApi = {
  getSystemInfo () {
    return request({
      url: '/system/info',
      method: 'get'
    })
  }
}
